import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { store } from '@/store/store';
import { hubClient } from '@tfpl/api-client';
import { getConfig } from '@/config';

export interface CostCategory {
  id: number;
  name: string;
}

export interface Auditor {
  id: number;
  fullName: string;
}

export interface User {
  id: number;
  name: string;
}

export interface Position {
  id: number;
  isPlayer: boolean;
  name: string;
  sendFeedback: boolean;
  gameLeague: hubClient.PositionsDto['gameLeague'];
  costCategories: CostCategory[];
  order: number;
  auditors: Auditor[];
  users: User[];
}

export enum PermissionsEnum {
  DASHBOARD = 'dashboard',
  SITDOWN = 'sitdown',
  PROJECTS = 'projects',
  OFFICE_ABSENCES = 'office-absences',
  GAME = 'game',
  WORKLOGS = 'work-logs',
  USERS = 'users',
  POSITIONS = 'positions',
  MEETING_TIMES = 'meeting-times',
  CONFIGURATION = 'configuration',
  SITDOWN_RATING = 'sitdown-rating',
  MANAGE_GAME = 'manage-game',
  MANAGE_SITDOWN_NOTES = 'manage-sitdown-notes',
  MANAGE_SITDOWNS = 'manage-sitdowns',
  MANAGE_ALL_GOALS = 'manage-all-goals',
  MANAGE_USER_GOALS = 'manage-user-goals',
  MY_GOALS = 'my-goals',
  MY_1ON1 = 'my-1on1',
  MANAGE_USER_1ON1 = 'manage-user-1on1',
  MANAGE_ALL_1ON1 = 'manage-all-1on1',
  OFFICE_ABSENCES_APPROVING = 'office-absences-approving',
  MANAGE_SUBSTITUTE_GROUPS = 'manage-substitute-groups',
  VIEW_OTHERS_WORKGLOGS = 'view-others-worklogs',
  MANAGE_USERS_BIO_INFO = 'manage-users-bio-info',
  MANAGE_USERS_PROJECTS_INFO = 'manage-users-projects-info',
  VIEW_COLLEAGUES = 'view-colleagues',
  ACHIEVEMENTS = 'achievements',
  MY_DOCUMENTS = 'my-documents',
  MANAGE_DOCUMENTS = 'manage-documents',
  VIEW_MY_FEEDBACKS = 'view-my-feedbacks',
  VIEW_USERS_FEEDBACKS = 'view-users-feedbacks',
  VIEW_ALL_FEEDBACKS = 'view-all-feedbacks',
  DEV_METRICS = 'dev-metrics',
}

enum PermissionSubjects {
  HUB_APP = 'HUB_APP',
  HUB_PROJECTS = 'HUB_PROJECTS',
  HUB_OFFICE_ABSENCES = 'HUB_OFFICE_ABSENCES',
  HUB_OFFICE_ABSENCES_REQUESTS = 'HUB_OFFICE_ABSENCES_REQUESTS',
  HUB_ACHIEVEMENTS = 'HUB_ACHIEVEMENTS',
  HUB_DASHBOARD = 'HUB_DASHBOARD',
  HUB_SITDOWN = 'HUB_SITDOWN',
  HUB_SITDOWN_RATING = 'HUB_SITDOWN_RATING',
  HUB_SITDOWN_GOALS = 'HUB_SITDOWN_GOALS',
  HUB_GAME = 'HUB_GAME',
  HUB_WORKLOGS = 'HUB_WORKLOGS',
  HUB_POSITIONS = 'HUB_POSITIONS',
  HUB_FEEDBACKS = 'HUB_FEEDBACKS',
  HUB_USERS = 'HUB_USERS',
  HUB_COLLEAGUES = 'HUB_COLLEAGUES',
  HUB_CONFIGURATION = 'HUB_CONFIGURATION',
  HUB_SUBSTITUTE_GROUPS = 'HUB_SUBSTITUTE_GROUPS',
  HUB_USERS_NOTES = 'HUB_USERS_NOTES',
  HUB_USERS_GOALS = 'HUB_USERS_GOALS',
  HUB_USERS_1ON1 = 'HUB_USERS_1ON1',
  HUB_COLLABORATOR_NOTES = 'HUB_COLLABORATOR_NOTES',
  HUB_DOCUMENTS = 'HUB_DOCUMENTS',
  HUB_SITDOWN_TIMES = 'HUB_SITDOWN_TIMES',
  HUB_WORK_TIME_RECORDS = 'HUB_WORK_TIME_RECORDS',
}
enum PermissionActions {
  LOGIN = 'LOGIN',
  READ_ALL = 'READ_ALL',
  READ_OWN = 'READ_OWN',
  READ_TEAM_MEMBER = 'READ_TEAM_MEMBER',
  MANAGE_ALL = 'MANAGE_ALL',
  MANAGE_OWN = 'MANAGE_OWN',
  MANAGE_TEAM_MEMBER = 'MANAGE_TEAM_MEMBER',
  MANAGE_BIO_ALL = 'MANAGE_BIO_ALL',
  MANAGE_PROJECT_INFO_ALL = 'MANAGE_PROJECT_INFO_ALL',
  UPDATE_ALL = 'UPDATE_ALL',
  UPDATE_OWN = 'UPDATE_OWN',
  UPDATE_STATUS = 'UPDATE_STATUS',
  CREATE = 'CREATE',
  READ = 'READ',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
}

export interface PermissionsDefinition {
  action: PermissionActions;
  subject: PermissionSubjects;
}

export const permissions = {
  [PermissionSubjects.HUB_APP]: {
    [PermissionActions.LOGIN]: {
      action: PermissionActions.LOGIN,
      subject: PermissionSubjects.HUB_APP,
    },
  },
  [PermissionSubjects.HUB_PROJECTS]: {
    [PermissionActions.READ_ALL]: {
      action: PermissionActions.READ_ALL,
      subject: PermissionSubjects.HUB_PROJECTS,
    },
    [PermissionActions.MANAGE_ALL]: {
      action: PermissionActions.MANAGE_ALL,
      subject: PermissionSubjects.HUB_PROJECTS,
    },
  },
  [PermissionSubjects.HUB_OFFICE_ABSENCES]: {
    [PermissionActions.READ_OWN]: {
      action: PermissionActions.READ_OWN,
      subject: PermissionSubjects.HUB_OFFICE_ABSENCES,
    },
    [PermissionActions.MANAGE_OWN]: {
      action: PermissionActions.MANAGE_OWN,
      subject: PermissionSubjects.HUB_OFFICE_ABSENCES,
    },
  },
  [PermissionSubjects.HUB_OFFICE_ABSENCES_REQUESTS]: {
    [PermissionActions.READ_OWN]: {
      action: PermissionActions.READ_OWN,
      subject: PermissionSubjects.HUB_OFFICE_ABSENCES_REQUESTS,
    },
    [PermissionActions.MANAGE_OWN]: {
      action: PermissionActions.MANAGE_OWN,
      subject: PermissionSubjects.HUB_OFFICE_ABSENCES_REQUESTS,
    },
  },
  [PermissionSubjects.HUB_ACHIEVEMENTS]: {
    [PermissionActions.READ_ALL]: {
      action: PermissionActions.READ_ALL,
      subject: PermissionSubjects.HUB_ACHIEVEMENTS,
    },
  },
  [PermissionSubjects.HUB_DASHBOARD]: {
    [PermissionActions.READ_ALL]: {
      action: PermissionActions.READ_ALL,
      subject: PermissionSubjects.HUB_DASHBOARD,
    },
  },
  [PermissionSubjects.HUB_SITDOWN]: {
    [PermissionActions.READ_ALL]: {
      action: PermissionActions.READ_ALL,
      subject: PermissionSubjects.HUB_SITDOWN,
    },
    [PermissionActions.MANAGE_ALL]: {
      action: PermissionActions.MANAGE_ALL,
      subject: PermissionSubjects.HUB_SITDOWN,
    },
  },
  [PermissionSubjects.HUB_SITDOWN_RATING]: {
    [PermissionActions.READ_ALL]: {
      action: PermissionActions.READ_ALL,
      subject: PermissionSubjects.HUB_SITDOWN_RATING,
    },
    [PermissionActions.MANAGE_ALL]: {
      action: PermissionActions.MANAGE_ALL,
      subject: PermissionSubjects.HUB_SITDOWN_RATING,
    },
  },
  [PermissionSubjects.HUB_SITDOWN_GOALS]: {
    [PermissionActions.READ_ALL]: {
      action: PermissionActions.READ_ALL,
      subject: PermissionSubjects.HUB_SITDOWN_GOALS,
    },
    [PermissionActions.MANAGE_ALL]: {
      action: PermissionActions.MANAGE_ALL,
      subject: PermissionSubjects.HUB_SITDOWN_GOALS,
    },
  },
  [PermissionSubjects.HUB_GAME]: {
    [PermissionActions.READ_ALL]: {
      action: PermissionActions.READ_ALL,
      subject: PermissionSubjects.HUB_GAME,
    },
    [PermissionActions.MANAGE_ALL]: {
      action: PermissionActions.MANAGE_ALL,
      subject: PermissionSubjects.HUB_GAME,
    },
  },
  [PermissionSubjects.HUB_WORKLOGS]: {
    [PermissionActions.READ_ALL]: {
      action: PermissionActions.READ_ALL,
      subject: PermissionSubjects.HUB_WORKLOGS,
    },
    [PermissionActions.READ_OWN]: {
      action: PermissionActions.READ_OWN,
      subject: PermissionSubjects.HUB_WORKLOGS,
    },
    [PermissionActions.MANAGE_OWN]: {
      action: PermissionActions.MANAGE_OWN,
      subject: PermissionSubjects.HUB_WORKLOGS,
    },
  },
  [PermissionSubjects.HUB_POSITIONS]: {
    [PermissionActions.READ_ALL]: {
      action: PermissionActions.READ_ALL,
      subject: PermissionSubjects.HUB_POSITIONS,
    },
    [PermissionActions.MANAGE_ALL]: {
      action: PermissionActions.MANAGE_ALL,
      subject: PermissionSubjects.HUB_POSITIONS,
    },
  },
  [PermissionSubjects.HUB_FEEDBACKS]: {
    [PermissionActions.READ_ALL]: {
      action: PermissionActions.READ_ALL,
      subject: PermissionSubjects.HUB_FEEDBACKS,
    },
    [PermissionActions.READ_TEAM_MEMBER]: {
      action: PermissionActions.READ_TEAM_MEMBER,
      subject: PermissionSubjects.HUB_FEEDBACKS,
    },
    [PermissionActions.READ_OWN]: {
      action: PermissionActions.READ_OWN,
      subject: PermissionSubjects.HUB_FEEDBACKS,
    },
  },
  [PermissionSubjects.HUB_USERS]: {
    [PermissionActions.READ_ALL]: {
      action: PermissionActions.READ_ALL,
      subject: PermissionSubjects.HUB_USERS,
    },
    [PermissionActions.MANAGE_ALL]: {
      action: PermissionActions.MANAGE_ALL,
      subject: PermissionSubjects.HUB_USERS,
    },
  },
  [PermissionSubjects.HUB_COLLEAGUES]: {
    [PermissionActions.READ_ALL]: {
      action: PermissionActions.READ_ALL,
      subject: PermissionSubjects.HUB_COLLEAGUES,
    },
    [PermissionActions.MANAGE_BIO_ALL]: {
      action: PermissionActions.MANAGE_BIO_ALL,
      subject: PermissionSubjects.HUB_COLLEAGUES,
    },
    [PermissionActions.MANAGE_PROJECT_INFO_ALL]: {
      action: PermissionActions.MANAGE_PROJECT_INFO_ALL,
      subject: PermissionSubjects.HUB_COLLEAGUES,
    },
  },
  [PermissionSubjects.HUB_CONFIGURATION]: {
    [PermissionActions.READ_ALL]: {
      action: PermissionActions.READ_ALL,
      subject: PermissionSubjects.HUB_CONFIGURATION,
    },
    [PermissionActions.MANAGE_ALL]: {
      action: PermissionActions.MANAGE_ALL,
      subject: PermissionSubjects.HUB_CONFIGURATION,
    },
  },
  [PermissionSubjects.HUB_SUBSTITUTE_GROUPS]: {
    [PermissionActions.READ_ALL]: {
      action: PermissionActions.READ_ALL,
      subject: PermissionSubjects.HUB_SUBSTITUTE_GROUPS,
    },
    [PermissionActions.MANAGE_ALL]: {
      action: PermissionActions.MANAGE_ALL,
      subject: PermissionSubjects.HUB_SUBSTITUTE_GROUPS,
    },
  },
  [PermissionSubjects.HUB_USERS_NOTES]: {
    [PermissionActions.READ_ALL]: {
      action: PermissionActions.READ_ALL,
      subject: PermissionSubjects.HUB_USERS_NOTES,
    },
    [PermissionActions.READ_OWN]: {
      action: PermissionActions.READ_OWN,
      subject: PermissionSubjects.HUB_USERS_NOTES,
    },
    [PermissionActions.READ_TEAM_MEMBER]: {
      action: PermissionActions.READ_TEAM_MEMBER,
      subject: PermissionSubjects.HUB_USERS_NOTES,
    },
    [PermissionActions.MANAGE_ALL]: {
      action: PermissionActions.MANAGE_ALL,
      subject: PermissionSubjects.HUB_USERS_NOTES,
    },
    [PermissionActions.MANAGE_TEAM_MEMBER]: {
      action: PermissionActions.MANAGE_TEAM_MEMBER,
      subject: PermissionSubjects.HUB_USERS_NOTES,
    },
  },
  [PermissionSubjects.HUB_USERS_1ON1]: {
    [PermissionActions.READ_OWN]: {
      action: PermissionActions.READ_OWN,
      subject: PermissionSubjects.HUB_USERS_1ON1,
    },
    [PermissionActions.READ_ALL]: {
      action: PermissionActions.READ_ALL,
      subject: PermissionSubjects.HUB_USERS_1ON1,
    },
    [PermissionActions.READ_TEAM_MEMBER]: {
      action: PermissionActions.READ_TEAM_MEMBER,
      subject: PermissionSubjects.HUB_USERS_1ON1,
    },
    [PermissionActions.MANAGE_ALL]: {
      action: PermissionActions.MANAGE_ALL,
      subject: PermissionSubjects.HUB_USERS_1ON1,
    },
    [PermissionActions.MANAGE_TEAM_MEMBER]: {
      action: PermissionActions.MANAGE_TEAM_MEMBER,
      subject: PermissionSubjects.HUB_USERS_1ON1,
    },
  },
  [PermissionSubjects.HUB_COLLABORATOR_NOTES]: {
    [PermissionActions.READ_ALL]: {
      action: PermissionActions.READ_ALL,
      subject: PermissionSubjects.HUB_COLLABORATOR_NOTES,
    },
    [PermissionActions.READ_TEAM_MEMBER]: {
      action: PermissionActions.READ_TEAM_MEMBER,
      subject: PermissionSubjects.HUB_COLLABORATOR_NOTES,
    },
    [PermissionActions.MANAGE_ALL]: {
      action: PermissionActions.MANAGE_ALL,
      subject: PermissionSubjects.HUB_COLLABORATOR_NOTES,
    },
    [PermissionActions.MANAGE_TEAM_MEMBER]: {
      action: PermissionActions.MANAGE_TEAM_MEMBER,
      subject: PermissionSubjects.HUB_COLLABORATOR_NOTES,
    },
  },
  [PermissionSubjects.HUB_USERS_GOALS]: {
    [PermissionActions.READ_OWN]: {
      action: PermissionActions.READ_OWN,
      subject: PermissionSubjects.HUB_USERS_GOALS,
    },
    [PermissionActions.READ_ALL]: {
      action: PermissionActions.READ_ALL,
      subject: PermissionSubjects.HUB_USERS_GOALS,
    },
    [PermissionActions.READ_TEAM_MEMBER]: {
      action: PermissionActions.READ_TEAM_MEMBER,
      subject: PermissionSubjects.HUB_USERS_GOALS,
    },
    [PermissionActions.MANAGE_ALL]: {
      action: PermissionActions.MANAGE_ALL,
      subject: PermissionSubjects.HUB_USERS_GOALS,
    },
    [PermissionActions.MANAGE_TEAM_MEMBER]: {
      action: PermissionActions.MANAGE_TEAM_MEMBER,
      subject: PermissionSubjects.HUB_USERS_GOALS,
    },
  },
  [PermissionSubjects.HUB_DOCUMENTS]: {
    [PermissionActions.READ_ALL]: {
      action: PermissionActions.READ_ALL,
      subject: PermissionSubjects.HUB_DOCUMENTS,
    },
    [PermissionActions.READ_OWN]: {
      action: PermissionActions.READ_OWN,
      subject: PermissionSubjects.HUB_DOCUMENTS,
    },
    [PermissionActions.MANAGE_ALL]: {
      action: PermissionActions.MANAGE_ALL,
      subject: PermissionSubjects.HUB_DOCUMENTS,
    },
    [PermissionActions.UPDATE_OWN]: {
      action: PermissionActions.UPDATE_OWN,
      subject: PermissionSubjects.HUB_DOCUMENTS,
    },
    [PermissionActions.UPDATE_STATUS]: {
      action: PermissionActions.UPDATE_STATUS,
      subject: PermissionSubjects.HUB_DOCUMENTS,
    },
  },
  [PermissionSubjects.HUB_SITDOWN_TIMES]: {
    [PermissionActions.READ]: {
      action: PermissionActions.READ,
      subject: PermissionSubjects.HUB_SITDOWN_TIMES,
    },
    [PermissionActions.CREATE]: {
      action: PermissionActions.CREATE,
      subject: PermissionSubjects.HUB_SITDOWN_TIMES,
    },
    [PermissionActions.UPDATE]: {
      action: PermissionActions.UPDATE,
      subject: PermissionSubjects.HUB_SITDOWN_TIMES,
    },
    [PermissionActions.DELETE]: {
      action: PermissionActions.DELETE,
      subject: PermissionSubjects.HUB_SITDOWN_TIMES,
    },
  },
  [PermissionSubjects.HUB_WORK_TIME_RECORDS]: {
    [PermissionActions.READ_OWN]: {
      action: PermissionActions.READ_OWN,
      subject: PermissionSubjects.HUB_WORK_TIME_RECORDS,
    },
    [PermissionActions.READ_ALL]: {
      action: PermissionActions.READ_ALL,
      subject: PermissionSubjects.HUB_WORK_TIME_RECORDS,
    },
    [PermissionActions.MANAGE_ALL]: {
      action: PermissionActions.MANAGE_ALL,
      subject: PermissionSubjects.HUB_WORK_TIME_RECORDS,
    },
    [PermissionActions.UPDATE_OWN]: {
      action: PermissionActions.UPDATE_OWN,
      subject: PermissionSubjects.HUB_WORK_TIME_RECORDS,
    },
    [PermissionActions.UPDATE_STATUS]: {
      action: PermissionActions.UPDATE_STATUS,
      subject: PermissionSubjects.HUB_WORK_TIME_RECORDS,
    },
  },
};

@Module({ name: 'PositionModule', store, dynamic: true, namespaced: true })
export class PositionModule extends VuexModule {
  /**************************************************************************
   * STATE
   *************************************************************************/
  private _loading = false;
  private _error = false;
  private _positions: Position[] = [];

  /**************************************************************************
   * GETTERS
   *************************************************************************/
  get positions(): Position[] {
    return this._positions;
  }

  get loading(): boolean {
    return this._loading;
  }

  get error(): boolean {
    return this._error;
  }

  /**************************************************************************
   * MUTATIONS
   *************************************************************************/
  @Mutation
  clearPositions(): void {
    this._positions = [];
  }

  @Mutation
  setPositions(positions: Position[]): void {
    this._positions = positions;
  }

  @Mutation
  setLoading(loading: boolean): void {
    this._loading = loading;
  }

  @Mutation
  setError(error: boolean): void {
    this._error = error;
  }

  /**************************************************************************
   * ACTIONS
   *************************************************************************/
  @Action({ rawError: true })
  public async fetchPositions(): Promise<void> {
    this.setLoading(true);
    try {
      const { data: response } = await hubClient.V3PositionsService.getManyV3({
        path: {
          tenant: getConfig().TENANT,
          company: getConfig().TENANT,
        },
        query: {
          limit: 999,
          offset: 0,
        },
        throwOnError: true,
      });

      const positions: Position[] = response.data.map((item) => {
        return {
          id: item.id,
          isPlayer: item.isPlayer,
          gameLeague: item.gameLeague,
          name: item.name,
          sendFeedback: item.sendFeedback,
          costCategories: item.costCategories,
          order: item.order,
          auditors: item.auditors.map((auditor) => ({
            id: auditor.id,
            fullName: auditor.name,
          })),
          users: item.users,
        };
      });

      this.setPositions(positions);
    } catch (err) {
      this.setError(true);
      console.error(err);
    } finally {
      this.setLoading(false);
    }
  }
}
export default getModule(PositionModule);
